import { useState } from "react";
import {
    Package,
    Workflow,
    Sparkles,
    Image as ImageIcon,
    MessageSquare,
    HelpCircle,
    Check,
    Star,
    Smartphone,
    Zap,
    PlugZap,
    Ban,
    SmartphoneNfc,
    TrendingUp,
    Palette,
    Quote,
} from "lucide-react";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../ui/accordion";
import { MEDIA, PHOTOS } from "../../lib/site";

const TABS = [
    { id: "products", label: "Products", icon: Package },
    { id: "how", label: "How it works", icon: Workflow },
    { id: "benefits", label: "Benefits", icon: Sparkles },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "reviews", label: "Reviews", icon: MessageSquare },
    { id: "faq", label: "FAQ", icon: HelpCircle },
];

export default function TabsSection() {
    const [active, setActive] = useState("products");

    return (
        <section
            id="explore"
            data-testid="tabs-section"
            className="relative pt-8 sm:pt-12 pb-20 sm:pb-28 border-t border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-10">
                {/* Tab nav */}
                <div className="overflow-x-auto -mx-6 px-6 mb-8">
                    <div
                        role="tablist"
                        data-testid="tabs-nav"
                        className="inline-flex items-center gap-1 p-1.5 rounded-2xl glass mx-auto"
                    >
                        {TABS.map((t) => {
                            const isActive = active === t.id;
                            return (
                                <button
                                    key={t.id}
                                    role="tab"
                                    aria-selected={isActive}
                                    data-testid={`tab-trigger-${t.id}`}
                                    onClick={() => setActive(t.id)}
                                    className={`flex items-center gap-2 px-4 sm:px-5 py-2.5 rounded-xl text-sm transition-all whitespace-nowrap ${
                                        isActive
                                            ? "bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.35)]"
                                            : "text-zinc-400 hover:text-white hover:bg-white/[0.05]"
                                    }`}
                                >
                                    <t.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Tab content */}
                <div data-testid={`tab-panel-${active}`} className="min-h-[420px] animate-fade-up">
                    {active === "products" && <ProductsTab />}
                    {active === "how" && <HowTab />}
                    {active === "benefits" && <BenefitsTab />}
                    {active === "gallery" && <GalleryTab />}
                    {active === "reviews" && <ReviewsTab />}
                    {active === "faq" && <FaqTab />}
                </div>
            </div>
        </section>
    );
}

/* ---------- Products ---------- */
const products = [
    {
        id: "standing",
        name: "NFC Standing Card",
        price: 45,
        tagline: "Counter-top stand-up",
        colors: ["Black", "White"],
        image: MEDIA.productStanding,
        features: [
            "Sits flat on any counter",
            "Pre-programmed for Google Reviews",
            "Black or White finish",
        ],
    },
    {
        id: "plaque",
        name: "NFC Plaque",
        price: 65,
        tagline: "Wall or counter plaque",
        colors: ["Black", "White"],
        featured: true,
        image: MEDIA.productPlaque,
        features: [
            "Premium plaque, mount or stand",
            "Pre-programmed for Google Reviews",
            "Black or White finish",
        ],
    },
    {
        id: "sticker",
        name: "NFC Sticker",
        price: 10,
        tagline: "Stick anywhere",
        colors: ["Default"],
        image: MEDIA.productSticker,
        features: [
            "Low-profile adhesive",
            "Perfect for tables, windows",
            "Discreet and durable",
        ],
    },
];

function ProductsTab() {
    return (
        <div className="grid md:grid-cols-3 gap-5">
            {products.map((p) => (
                <div
                    key={p.id}
                    data-testid={`product-card-${p.id}`}
                    className={`group relative rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-1 ${
                        p.featured
                            ? "bg-gradient-to-b from-blue-600/10 to-white/[0.02] border border-blue-500/30 shadow-[0_0_40px_rgba(37,99,235,0.12)]"
                            : "bg-white/[0.03] border border-white/10"
                    }`}
                >
                    {p.featured && (
                        <div className="absolute top-4 right-4 z-10 px-2.5 py-1 rounded-full bg-blue-600 text-white text-[9px] font-semibold tracking-wider flex items-center gap-1">
                            <Sparkles className="w-2.5 h-2.5" />
                            POPULAR
                        </div>
                    )}
                    <div className="relative aspect-[5/4] overflow-hidden bg-zinc-900">
                        <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    </div>
                    <div className="p-5">
                        <div className="flex items-baseline justify-between mb-1">
                            <h3 className="font-display text-lg font-normal">{p.name}</h3>
                            <div className="font-display text-2xl font-light">${p.price}</div>
                        </div>
                        <p className="text-xs text-zinc-500 mb-3">{p.tagline}</p>
                        {p.colors.length > 1 && (
                            <div className="flex items-center gap-1.5 mb-3">
                                <Palette className="w-3 h-3 text-zinc-500" />
                                {p.colors.map((c) => (
                                    <span key={c} className="text-[10px] text-zinc-300 border border-white/15 rounded-full px-1.5 py-0.5">{c}</span>
                                ))}
                            </div>
                        )}
                        <ul className="space-y-1.5">
                            {p.features.map((f) => (
                                <li key={f} className="flex items-start gap-2 text-xs text-zinc-300">
                                    <Check className={`w-3 h-3 mt-0.5 shrink-0 ${p.featured ? "text-blue-400" : "text-zinc-400"}`} strokeWidth={2.2} />
                                    {f}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---------- How it works ---------- */
function HowTab() {
    const steps = [
        { n: "01", title: "Customer Taps", desc: "They hover their phone over your NFC plaque.", icon: Smartphone },
        { n: "02", title: "Action Opens", desc: "Your review page, menu, or link loads instantly.", icon: Zap },
        { n: "03", title: "Business Grows", desc: "More 5-star reviews, more menu views, on autopilot.", icon: TrendingUp },
    ];
    return (
        <div className="grid lg:grid-cols-12 gap-5">
            <div className="lg:col-span-7 grid gap-4">
                {steps.map((s, i) => (
                    <div key={s.n} data-testid={`how-step-${i + 1}`} className="glass rounded-2xl p-6 flex items-start gap-5 hover:bg-white/[0.06] transition-colors">
                        <div className="w-12 h-12 rounded-xl bg-blue-600/10 border border-blue-500/30 flex items-center justify-center shrink-0">
                            <s.icon className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                                <span className="font-display text-[11px] text-blue-500 tracking-[0.2em]">STEP {s.n}</span>
                                <span className="h-px flex-1 bg-white/10" />
                            </div>
                            <h3 className="font-display text-xl font-normal">{s.title}</h3>
                            <p className="text-sm text-zinc-400 mt-1">{s.desc}</p>
                        </div>
                    </div>
                ))}
            </div>
            <div className="lg:col-span-5 relative rounded-2xl overflow-hidden glass min-h-[280px]">
                <img src={MEDIA.mockupUi} alt="Tap demo" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="label-eyebrow mb-1">Live demo</div>
                    <div className="font-display text-xl font-light">Tap → load → review in 4 seconds.</div>
                </div>
            </div>
        </div>
    );
}

/* ---------- Benefits ---------- */
function BenefitsTab() {
    const benefits = [
        { icon: Star, title: "More 5-Star Reviews", desc: "Customers tap, leave a review, walk out.", span: "md:col-span-2" },
        { icon: Zap, title: "Lightning Fast", desc: "Tap-to-open in under 1 second." },
        { icon: Sparkles, title: "Modern & Professional", desc: "Premium finish that elevates your brand." },
        { icon: PlugZap, title: "60-Second Setup", desc: "Place it. Done. We pre-program it." },
        { icon: Ban, title: "No App Required", desc: "Works natively on every iPhone & Android." },
        { icon: SmartphoneNfc, title: "Universal Compatibility", desc: "Same NFC tech as Apple Pay & Google Wallet.", span: "md:col-span-2" },
    ];
    return (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {benefits.map((b, i) => (
                <div key={b.title} data-testid={`benefit-${i}`} className={`glass rounded-2xl p-6 hover:bg-white/[0.06] hover:-translate-y-0.5 transition-all ${b.span || ""}`}>
                    <div className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-500/30 flex items-center justify-center mb-4">
                        <b.icon className="w-4 h-4 text-blue-400" strokeWidth={1.5} />
                    </div>
                    <h3 className="font-display text-lg font-normal">{b.title}</h3>
                    <p className="text-xs text-zinc-400 mt-1.5 leading-relaxed">{b.desc}</p>
                </div>
            ))}
        </div>
    );
}

/* ---------- Gallery ---------- */
function GalleryTab() {
    const items = [
        { src: PHOTOS.plaques[0], label: "Plaque — Black" },
        { src: PHOTOS.stands[0], label: "Standing card" },
        { src: PHOTOS.stickers[0], label: "NFC sticker" },
        { src: PHOTOS.plaques[1], label: "Plaque — alt finish" },
        { src: PHOTOS.stands[1], label: "Stand in use" },
        { src: PHOTOS.stickers[1], label: "Sticker — flat" },
        { src: PHOTOS.plaques[2], label: "Plaque detail" },
        { src: PHOTOS.stands[2], label: "Stand — white" },
        { src: PHOTOS.plaques[3], label: "Plaque mounted" },
        { src: PHOTOS.stickers[2], label: "Sticker placement" },
        { src: PHOTOS.stands[3], label: "Stand close-up" },
        { src: PHOTOS.plaques[4], label: "Plaque premium" },
    ];
    return (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
            {items.map((it, i) => (
                <div key={i} data-testid={`gallery-item-${i}`} className="group relative rounded-xl overflow-hidden border border-white/10 bg-zinc-900 aspect-square">
                    <img src={it.src} alt={it.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                    <div className="absolute bottom-2.5 left-3 right-3">
                        <div className="text-[9px] text-blue-300 tracking-widest uppercase">NFC Pro</div>
                        <div className="text-[11px] font-medium text-white">{it.label}</div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---------- Reviews ---------- */
function ReviewsTab() {
    const items = [
        { name: "Marcus T.", biz: "Brew & Bean Coffee", text: "We went from 3 reviews a month to 22. Customers love tapping it on the counter." },
        { name: "Sara L.", biz: "The Hairline Studio", text: "My plaque looks beautiful on the front desk. Bookings have noticeably gone up." },
        { name: "Diego R.", biz: "Taco Vista", text: "Best money I've ever spent. We get 5-star reviews almost every shift now." },
        { name: "Emily K.", biz: "Bloom Nails", text: "Setup took less than a minute. Clients are obsessed with the tap-to-Instagram link." },
        { name: "Jordan P.", biz: "Cut Above Barbers", text: "Looks premium, works flawlessly. Pays for itself in one good review week." },
        { name: "Priya M.", biz: "Verde Yoga", text: "Zero tech know-how needed. My members love how seamless it is." },
    ];
    return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((t, i) => (
                <div key={i} data-testid={`review-${i}`} className="glass rounded-2xl p-6 hover:bg-white/[0.06] transition-colors">
                    <Quote className="w-5 h-5 text-blue-500/60 mb-3" strokeWidth={1.5} />
                    <p className="text-sm text-zinc-200 leading-relaxed">&ldquo;{t.text}&rdquo;</p>
                    <div className="mt-5 flex items-center justify-between">
                        <div>
                            <div className="text-sm font-medium text-white">{t.name}</div>
                            <div className="text-[11px] text-zinc-500">{t.biz}</div>
                        </div>
                        <div className="flex items-center gap-0.5">
                            {[...Array(5)].map((_, i) => (<Star key={i} className="w-3 h-3 fill-yellow-400 text-yellow-400" />))}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ---------- FAQ ---------- */
function FaqTab() {
    const faqs = [
        { q: "How does the NFC plaque actually work?", a: "Inside every NFC Pro plaque is a programmable chip. When a smartphone is held within 1-2 inches of it, the phone reads a small URL and opens it instantly — review page, menu, Instagram, anything." },
        { q: "Do customers need an app?", a: "No. Every modern iPhone (XS+) and Android has NFC built in and reads our plaques natively. Nothing to download." },
        { q: "Can I change the link later?", a: "Yes. The chip is reprogrammable. Send us a message and we'll update it to point anywhere you'd like." },
        { q: "What's included with each product?", a: "All three formats — Standing Card ($45), Plaque ($65), and Sticker ($10) — come pre-programmed for Google Reviews. Standing Cards and Plaques are available in Black or White." },
        { q: "How long does it take to receive my order?", a: "Orders ship within 2-3 business days. We'll send tracking info to your email when it goes out." },
        { q: "Where should I place the NFC product?", a: "Anywhere customers naturally pause — the checkout counter, front desk, table, or window. Stickers for low-profile spots, standing cards for counters, plaques for walls." },
    ];
    return (
        <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-2.5">
                {faqs.map((item, i) => (
                    <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`} className="glass rounded-2xl border-white/10 px-5 data-[state=open]:bg-white/[0.06] transition-colors">
                        <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left font-display text-base font-normal hover:no-underline py-4">
                            {item.q}
                        </AccordionTrigger>
                        <AccordionContent className="text-sm text-zinc-400 leading-relaxed pb-4">
                            {item.a}
                        </AccordionContent>
                    </AccordionItem>
                ))}
            </Accordion>
        </div>
    );
}
